import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock,
  MapPin,
  Users,
  Calendar,
  UserCheck,
  UserX,
  AlertTriangle,
  Plus,
  FileText,
  Trash2,
  Eye,
  Download,
  RefreshCw
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceManager = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayData, setTodayData] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Dialog states
  const [addLeaveOpen, setAddLeaveOpen] = useState(false);
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [trackEmployeeOpen, setTrackEmployeeOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  
  // Form states
  const [leaveForm, setLeaveForm] = useState({
    employee_id: '',
    leave_type: '',
    from_date: '',
    to_date: '',
    reason: ''
  });

  const fetchTodayAttendance = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTodayData(data);
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    }
  }, [token]);

  const fetchLeaves = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attendance/leaves`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaves(data);
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/employees/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.filter(e => e.status === 'Active'));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, [token]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attendance/leave-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  }, [token]);

  const fetchMonthlyReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/attendance/report?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMonthlyReport(data);
      }
    } catch (error) {
      console.error('Error fetching monthly report:', error);
    } finally {
      setReportLoading(false);
    }
  }, [token, selectedMonth]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTodayAttendance(),
        fetchLeaves(),
        fetchEmployees(),
        fetchLeaveTypes()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchTodayAttendance, fetchLeaves, fetchEmployees, fetchLeaveTypes]);

  const handleAddLeave = async () => {
    if (!leaveForm.employee_id || !leaveForm.leave_type || !leaveForm.from_date || !leaveForm.to_date || !leaveForm.reason) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/attendance/leaves?employee_id=${leaveForm.employee_id}&leave_type=${encodeURIComponent(leaveForm.leave_type)}&from_date=${leaveForm.from_date}&to_date=${leaveForm.to_date}&reason=${encodeURIComponent(leaveForm.reason)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Leave added successfully');
        setAddLeaveOpen(false);
        setLeaveForm({ employee_id: '', leave_type: '', from_date: '', to_date: '', reason: '' });
        fetchLeaves();
        fetchTodayAttendance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add leave');
      }
    } catch (error) {
      toast.error('Error adding leave');
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave?')) return;

    try {
      const response = await fetch(`${API_URL}/api/attendance/leaves/${leaveId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Leave deleted successfully');
        fetchLeaves();
        fetchTodayAttendance();
      } else {
        toast.error('Failed to delete leave');
      }
    } catch (error) {
      toast.error('Error deleting leave');
    }
  };

  const handleTrackEmployee = async (employeeId) => {
    try {
      const response = await fetch(`${API_URL}/api/attendance/track/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrackingData(data);
        setTrackEmployeeOpen(true);
      } else {
        toast.error('Failed to fetch tracking data');
      }
    } catch (error) {
      toast.error('Error tracking employee');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-100 text-green-800';
      case 'Leave': return 'bg-blue-100 text-blue-800';
      case 'Half Day': return 'bg-yellow-100 text-yellow-800';
      case 'Absent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${suffix}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Manager</h1>
          <p className="text-gray-600">Monitor employee attendance, leaves, and location</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => {
            fetchTodayAttendance();
            fetchLeaves();
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {todayData && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-gray-600">Total Employees</span>
              </div>
              <p className="text-2xl font-bold mt-2">{todayData.summary.total_employees}</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-700">Present</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-green-700">{todayData.summary.present}</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-blue-700">On Leave</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-blue-700">{todayData.summary.on_leave}</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <UserX className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700">Absent</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-red-700">{todayData.summary.absent}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-emerald-600" />
                <span className="text-sm text-emerald-700">Checked In</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-emerald-700">{todayData.summary.currently_checked_in}</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <span className="text-sm text-orange-700">Late Checkouts</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-orange-700">{todayData.summary.late_checkouts}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="today" className="space-y-4">
        <TabsList>
          <TabsTrigger value="today">Today&apos;s Attendance</TabsTrigger>
          <TabsTrigger value="leaves">Leave Management</TabsTrigger>
          <TabsTrigger value="reports">Monthly Reports</TabsTrigger>
        </TabsList>

        {/* Today's Attendance Tab */}
        <TabsContent value="today" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Today&apos;s Attendance - {todayData?.date}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Check In</TableHead>
                    <TableHead>Check Out</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {todayData?.attendance_records?.length > 0 ? (
                    todayData.attendance_records.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">
                          {record.employee?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>{record.employee?.role}</TableCell>
                        <TableCell>
                          <div>
                            <span>{formatTime(record.check_in_time)}</span>
                            {record.check_in_location && (
                              <MapPin className="h-3 w-3 inline ml-1 text-green-600" title="Location captured" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {record.check_out_time ? (
                            <div>
                              <span>{formatTime(record.check_out_time)}</span>
                              {record.check_out_location && (
                                <MapPin className="h-3 w-3 inline ml-1 text-green-600" title="Location captured" />
                              )}
                              {record.late_check_out && (
                                <AlertTriangle className="h-3 w-3 inline ml-1 text-orange-600" title="Late checkout" />
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {record.total_hours ? (
                            <div>
                              <span>{record.total_hours}h</span>
                              {record.overtime_hours > 0 && (
                                <span className="text-xs text-green-600 ml-1">(+{record.overtime_hours}h OT)</span>
                              )}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(record.status)}>
                              {record.status}
                            </Badge>
                            {(record.is_unusual_checkin || record.is_unusual_checkout) && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">
                                ⚠ Unusual
                              </Badge>
                            )}
                          </div>
                          {record.unusual_checkin_reason && (
                            <p className="text-xs text-amber-600 mt-1">{record.unusual_checkin_reason}</p>
                          )}
                          {record.unusual_checkout_reason && (
                            <p className="text-xs text-amber-600 mt-1">{record.unusual_checkout_reason}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTrackEmployee(record.employee_id)}
                            title="Track Location"
                          >
                            <MapPin className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                        No attendance records for today
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Absent Employees */}
              {todayData?.absent?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <UserX className="h-4 w-4" />
                    Absent Employees ({todayData.absent.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {todayData.absent.map((emp) => (
                      <Badge key={emp.id} variant="outline" className="border-red-300 text-red-700">
                        {emp.name} ({emp.role})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* On Leave Employees */}
              {todayData?.on_leave?.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    On Leave ({todayData.on_leave.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {todayData.on_leave.map((emp) => (
                      <Badge key={emp.id} variant="outline" className="border-blue-300 text-blue-700">
                        {emp.name} ({emp.role})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leave Management Tab */}
        <TabsContent value="leaves" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Leave Records
              </CardTitle>
              <Button onClick={() => setAddLeaveOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Leave
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.length > 0 ? (
                    leaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="font-medium">
                          {leave.employee?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{leave.leave_type}</Badge>
                        </TableCell>
                        <TableCell>{leave.from_date}</TableCell>
                        <TableCell>{leave.to_date}</TableCell>
                        <TableCell>{leave.total_days}</TableCell>
                        <TableCell className="max-w-xs truncate" title={leave.reason}>
                          {leave.reason}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteLeave(leave.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                        No leave records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Monthly Attendance Report
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-40"
                />
                <Button onClick={fetchMonthlyReport} disabled={reportLoading}>
                  {reportLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  <span className="ml-2">Generate</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyReport ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>Working Days:</strong> {monthlyReport.settings.total_working_days} |
                      <strong className="ml-2">Standard Hours:</strong> {monthlyReport.settings.standard_working_hours}h/day
                    </p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Present</TableHead>
                        <TableHead>Leave</TableHead>
                        <TableHead>Absent</TableHead>
                        <TableHead>Hours Worked</TableHead>
                        <TableHead>Overtime</TableHead>
                        <TableHead>Attendance %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyReport.report.map((item) => (
                        <TableRow key={item.employee.id}>
                          <TableCell className="font-medium">{item.employee.name}</TableCell>
                          <TableCell>{item.employee.role}</TableCell>
                          <TableCell className="text-green-700">{item.summary.present_days}</TableCell>
                          <TableCell className="text-blue-700">{item.summary.leave_days}</TableCell>
                          <TableCell className="text-red-700">{item.summary.absent_days}</TableCell>
                          <TableCell>{item.summary.total_hours_worked}h</TableCell>
                          <TableCell className="text-orange-700">{item.summary.overtime_hours}h</TableCell>
                          <TableCell>
                            <Badge className={item.summary.attendance_percentage >= 80 ? 'bg-green-100 text-green-800' : item.summary.attendance_percentage >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                              {item.summary.attendance_percentage}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  Select a month and click Generate to view the report
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Leave Dialog */}
      <Dialog open={addLeaveOpen} onOpenChange={setAddLeaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Leave Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select
                value={leaveForm.employee_id}
                onValueChange={(value) => setLeaveForm({ ...leaveForm, employee_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Leave Type</Label>
              <Select
                value={leaveForm.leave_type}
                onValueChange={(value) => setLeaveForm({ ...leaveForm, leave_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={leaveForm.from_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={leaveForm.to_date}
                  min={leaveForm.from_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder="Enter reason for leave"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLeaveOpen(false)}>Cancel</Button>
            <Button onClick={handleAddLeave}>Add Leave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Track Employee Dialog */}
      <Dialog open={trackEmployeeOpen} onOpenChange={setTrackEmployeeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Employee Location Tracking
            </DialogTitle>
          </DialogHeader>
          {trackingData && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold">{trackingData.employee?.name}</p>
                <p className="text-sm text-gray-600">{trackingData.employee?.role} - {trackingData.employee?.designation}</p>
                <p className="text-sm mt-2">
                  Status: <Badge className={trackingData.current_status === 'checked_in' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {trackingData.current_status === 'checked_in' ? 'Currently Working' : 'Checked Out'}
                  </Badge>
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Recent Locations</h4>
                {trackingData.last_known_locations?.length > 0 ? (
                  <div className="space-y-2">
                    {trackingData.last_known_locations.map((loc, idx) => (
                      <div key={idx} className="border rounded-lg p-3 text-sm">
                        <div className="flex justify-between">
                          <Badge variant="outline" className="capitalize">{loc.type?.replace('_', ' ')}</Badge>
                          <span className="text-gray-500">{loc.time}</span>
                        </div>
                        {loc.location && (
                          <p className="mt-1 text-gray-600">
                            Lat: {loc.location.latitude?.toFixed(6)}, Long: {loc.location.longitude?.toFixed(6)}
                          </p>
                        )}
                        {loc.entity && (
                          <p className="text-xs text-gray-500 mt-1">{loc.entity}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No location data available</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrackEmployeeOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceManager;
